import { useState } from "react";
import { useNavigate } from "react-router";
import { BatchCheckInModal } from "@/features/batches/components/BatchCheckInModal";
import { hasStructuredDisplayId, renderBatchLabel } from "@/lib/formatters";
import { usePoll } from "@/hooks/usePoll";
import { useCurrentBatch } from "../hooks/useCurrentBatch";
import { useFeedLock } from "../hooks/useFeedLock";
import "./BatchContextBanner.css";

/**
 * Shown at the top of `QueueDashboardPage` when the operator currently holds
 * one or more live document locks. Surfaces the batch's structured displayId
 * (with the free-form name as a muted sub-line, matching BatchList +
 * BatchDetail), completion counts, and fast-path actions (continue keying /
 * release the batch).
 */
export function BatchContextBanner() {
  const { data, refetch } = useCurrentBatch();
  // R-159 — banner shows live progress on the operator's current batch
  // (completion counts, ready-to-release state), so poll at the same cadence
  // as BatchDetailPage (8s) — both surface the same per-batch state and the
  // operator might be looking at either. Paused when tab is hidden.
  usePoll(refetch, 8_000);
  // R-162 — surface the active feed lock as an inline chip
  const { lock: feedLock } = useFeedLock();
  const [checkInOpen, setCheckInOpen] = useState(false);
  const navigate = useNavigate();

  if (!data.batch) return null;

  const isStructured = hasStructuredDisplayId(data.batch);

  return (
    <div className="batch-context-banner" role="status">
      <div className="batch-context-banner__summary">
        You're on Batch{" "}
        <strong
          className={
            isStructured
              ? "batch-context-banner__display-id"
              : "batch-context-banner__display-id batch-context-banner__display-id--fallback"
          }
          data-testid="batch-context-banner-display-id"
        >
          {/* R-167 WU-7 — customDisplayLabel ?? displayId ?? cuid. */}
          {renderBatchLabel(data.batch)}
        </strong>
        {data.batch.name && (
          <span
            className="batch-context-banner__name-sub"
            data-testid="batch-context-banner-name-sub"
          >
            {data.batch.name}
          </span>
        )}
        {" — "}
        {data.batch.completedDocs} of {data.batch.totalDocs} documents keyed.
        {feedLock && (
          <span
            className="batch-context-banner__feed-chip"
            aria-label={`Feeding ${feedLock.stepType}`}
            data-testid="batch-context-banner-feed-chip"
          >
            🔒 {feedLock.stepType}
          </span>
        )}
      </div>
      <div className="batch-context-banner__actions">
        <button
          type="button"
          className="batch-context-banner__btn batch-context-banner__btn--primary"
          onClick={() => navigate("/queues/next")}
        >
          Continue
        </button>
        <button
          type="button"
          className="batch-context-banner__btn"
          onClick={() => setCheckInOpen(true)}
        >
          Check In
        </button>
      </div>
      <BatchCheckInModal
        batchId={data.batch.id}
        open={checkInOpen}
        onClose={() => setCheckInOpen(false)}
        onSuccess={() => {
          setCheckInOpen(false);
          void refetch();
        }}
      />
    </div>
  );
}
